<template>
  <div class="h-full">
    <div class="h-full flex flex-col">
      <div class="flex w-full pb-3 items-center flex-shrink-0 sm:pr-10 justify-between">
        <div class="text-13 font-600">{{ $t('navigation.integration_hub') }}</div>
        <div class="flex gap-x-1 items-center select-none">
          <span class="text-11 font-500 mr-1">Filters:</span>
          <div
            v-for="filter in filters"
            :key="filter.value"
            class="text-11 cursor-pointer border border-stroke border-solid rounded-8 px-2 py-0.5"
            :class="{ 'bg-primary text-surface-light': selectedFilter === filter.value, 'bg-white text-dark-text': selectedFilter !== filter.value }"
            @click="selectedFilter = filter.value"
          >
            {{ filter.label }}
          </div>
        </div>
      </div>
      <div class="w-full pb-10">
        <div class="w-full pr-3 flex-grow overflow-auto sm:pr-10 pl-2 pb-2">
          <div v-if="integrations.length" class="pl-1 font-500 mb-2 text-13">Active</div>
          <div v-if="integrations.length" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xxl:grid-cols-5 pt-1">
            <ListCard v-for="(integration, key) in integrations" :key="key" height-class="h-18" class="!px-3 py-2 bg-white rounded-20 cursor-pointer relative" @click="onAdapterClick(integration)">
              <div v-if="isDisabled(integration)" class="absolute right-0 top-0 text-10 font-500 tracking-wide bg-gray-500 text-off-white px-3 rounded-tr-20 rounded-bl-20">Disabled</div>
              <div class="flex items-center w-full">
                <Icon v-if="integration.iconName" :name="integration.iconName" class="w-7 h-7 mr-2 ml-2 flex-shrink-0 text-primary" />
                <img v-else :src="integration.icon" class="w-7 h-7 mr-2 ml-2 flex-shrink-0" :alt="integration.name" />
                <div class="flex items-start truncate text-13 flex-col">
                  <span class="font-500 mr-2 truncate">{{ integration.name }}</span>
                  <span class="text-11 mr-2 truncate capitalize text-gray-500">{{ $t(`projects.integration_hub.${integration.listType}`) }}</span>
                </div>
              </div>
            </ListCard>
          </div>
          <div class="pl-1 font-500 mb-2 mt-6 text-13">Available</div>
          <div v-if="!availableIntegrations.length" class="text-center text-semi-dark-text text-12">No available options</div>
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 xxl:grid-cols-5 pt-1">
            <div v-for="(integration, key) in availableIntegrations" :key="key" class="relative">
              <div v-if="!integration.enabled" class="absolute right-0 z-1 top-0 text-10 font-500 tracking-wide bg-gray-500 text-off-white px-3 rounded-tr-20 rounded-bl-20">Soon</div>
              <ListCard
                :class="{
                  'cursor-pointer': integration.enabled,
                  'opacity-50': !integration.enabled,
                }"
                height-class="h-18"
                class="!px-3 py-2 bg-white rounded-20 relative select-none"
                @click="integration.enabled && onConnectClick(integration)"
              >
                <div class="flex items-center w-full">
                  <Icon v-if="integration.iconName" :name="integration.iconName" class="w-7 h-7 mr-2 ml-2 flex-shrink-0 text-primary" />
                  <img v-else :src="integration.icon" class="w-7 h-7 mr-2 ml-2 flex-shrink-0" :alt="integration.name" />
                  <div class="flex items-start truncate text-13 flex-col">
                    <span class="font-500 mr-2 truncate">{{ integration.name }}</span>
                    <span class="text-11 mr-2 truncate capitalize text-gray-500">{{ $t(`projects.integration_hub.${integration.listType}`) }}</span>
                  </div>
                </div>
              </ListCard>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import DeleteModal from '../../modals/DeleteModal.vue';
import { DATA_ADAPTERS, DATA_ADAPTER_ICONS as ICONS } from '../../../constants';
import ConnectDataAdapter from '../../project/ConnectDataAdapter.vue';
import CreateAIProviderModal from '../../project/CreateAIProviderModal.vue';
import CreateEmailProviderModal from '../../project/CreateEmailProviderModal.vue';
import CreateLoggingProviderModal from '../../project/CreateLoggingProviderModal.vue';

const aiProviderIcon = {
  ANTHROPIC: 'claude.png',
  AZURE_OPENAI: 'azure.png',
  GOOGLE: 'gemini.webp',
  OPENAI: 'openai.png',
  CLOUDFLARE_AI: 'cloudflare.png',
  PERPLEXITY: 'perplexity.png',
  LM_STUDIO: 'lmstudio.webp',
};

const emailProviderIcon = {
  AWS_SES: 'AWS_SES.png',
  SENDGRID: 'SENDGRID.png',
};

const loggingProviderIconName = {
  NEW_RELIC: 'newrelic.png',
};

export default {
  name: 'IntegrationHub',
  data() {
    return {
      fetching: false,
      selectedFilter: 'all',
      availableAuthProviders: {
        FIREBASE: {
          name: 'Firebase',
          key: 'FIREBASE',
          icon: '/img/dataadapters/firebase_authentication.png',
          enabled: true,
        },
        API_KEY: {
          name: 'API Key',
          key: 'API_KEY',
          icon: '/img/dataadapters/apikey.png',
          enabled: true,
        },
      },
    };
  },
  computed: {
    ...mapState(['user', 'availableDataAdapters', 'availableAIProviders', 'availableEmailProviders', 'availableLoggingProviders']),
    ...mapGetters(['getProjectDataTypes', 'getProjectDataAdapters', 'getProjectAIProviders', 'getProjectEmailProviders', 'getProjectAuthProviders', 'getProjectLoggingProviders']),
    filters() {
      return [
        { label: 'All', value: 'all' },
        { label: 'Data Adapters', value: 'data_adapter' },
        { label: 'AI Providers', value: 'ai_provider' },
        { label: 'Email Providers', value: 'email_provider' },
        { label: 'Auth Providers', value: 'auth_provider' },
        { label: 'Logging', value: 'logging_provider' },
      ];
    },
    projectId() {
      return this.$route.params.projectId;
    },
    integrations() {
      const dataAdapters = Object.values(this.getProjectDataAdapters()).map((adapter) => {
        adapter.name = adapter.payload?.name || this.availableDataAdapters[adapter.type]?.name;
        adapter.key = adapter.adapter_type;
        adapter.listType = 'data_adapter';
        adapter.icon = ICONS[adapter.type];
        return adapter;
      });
      const aiProviders = Object.values(this.getProjectAIProviders()).map((provider) => {
        provider.name = this.availableAIProviders[provider.provider]?.name;
        provider.listType = 'ai_provider';
        provider.icon = `/img/providers/${aiProviderIcon[provider.provider]}` || '';
        return provider;
      });
      const emailProviders = Object.values(this.getProjectEmailProviders()).map((provider) => {
        provider.name = this.availableEmailProviders[provider.provider]?.name;
        provider.listType = 'email_provider';
        provider.key = provider.provider;
        provider.icon = `/img/providers/${emailProviderIcon[provider.provider]}` || '';
        return provider;
      });
      const authProviders = Object.values(this.getProjectAuthProviders()).map((provider) => {
        provider.name = this.availableAuthProviders[provider.provider]?.name;
        provider.listType = 'auth_provider';
        provider.icon = this.availableAuthProviders[provider.provider]?.icon;
        provider.key = provider.provider;
        return provider;
      });
      const loggingProviders = Object.values(this.getProjectLoggingProviders()).map((provider) => {
        provider.name = this.availableLoggingProviders[provider.provider]?.name;
        provider.listType = 'logging_provider';
        provider.icon = `/img/providers/${loggingProviderIconName[provider.provider]}` || '';
        provider.key = provider.provider;
        return provider;
      });

      return [...dataAdapters, ...aiProviders, ...emailProviders, ...authProviders, ...loggingProviders].filter((item) => item.listType === this.selectedFilter || this.selectedFilter === 'all');
    },
    availableIntegrations() {
      const dataAdapterCatalog = Object.keys(this.availableDataAdapters).length
        ? this.availableDataAdapters
        : { POSTGRESQL: DATA_ADAPTERS.POSTGRESQL, MYSQL: DATA_ADAPTERS.MYSQL, MSSQL: DATA_ADAPTERS.MSSQL, CLOUDFLARE_D1: DATA_ADAPTERS.CLOUDFLARE_D1, DATABRICKS: DATA_ADAPTERS.DATABRICKS };
      const dataAdapters = Object.keys(dataAdapterCatalog)
        .filter((key) => !this.getProjectDataAdapters()[key])
        .map((key) => {
          const adapter = { ...dataAdapterCatalog[key] };
          adapter.listType = 'data_adapter';
          adapter.icon = ICONS[key];
          adapter.enabled = adapter.enabled === true;
          return adapter;
        });

      const aiProviders = Object.keys(this.availableAIProviders)
        .map((key) => {
          const provider = this.availableAIProviders[key];
          provider.listType = 'ai_provider';
          provider.icon = `/img/providers/${aiProviderIcon[key]}` || '';
          return provider;
        })
        .filter((provider) => !this.getProjectAIProviders()[provider.key]);

      const emailProviders = Object.keys(this.availableEmailProviders)
        .map((key) => {
          const provider = this.availableEmailProviders[key];
          provider.listType = 'email_provider';
          provider.icon = `/img/providers/${emailProviderIcon[key]}` || '';
          return provider;
        })
        .filter((provider) => !this.getProjectEmailProviders()[provider.key]);

      const authProviders = Object.keys(this.availableAuthProviders)
        .map((key) => {
          const provider = this.availableAuthProviders[key];
          provider.listType = 'auth_provider';
          provider.icon = this.availableAuthProviders[key].icon;
          provider.key = key;
          return provider;
        })
        .filter((provider) => !this.getProjectAuthProviders()[provider.key]);

      const loggingProviders = Object.keys(this.availableLoggingProviders)
        .filter((provider) => !this.getProjectLoggingProviders()[provider.key])
        .map((key) => {
          const provider = this.availableLoggingProviders[key];
          provider.listType = 'logging_provider';
          provider.icon = `/img/providers/${loggingProviderIconName[key]}` || '';
          return provider;
        });

      return [...dataAdapters, ...aiProviders, ...emailProviders, ...authProviders, ...loggingProviders].filter((item) => item.listType === this.selectedFilter || this.selectedFilter === 'all');
    },
  },
  methods: {
    ...mapActions([
      'showToastMessage',
      'fetchProjectDataTypes',
      'fetchProjectDataAdapters',
      'fetchProjectAIProviders',
      'fetchProjectEmailProviders',
      'fetchProjectAuthProviders',
      'fetchProjectLoggingProviders',
      'deleteProjectAIProvider',
      'deleteProjectEmailProvider',
    ]),
    isDisabled(adapter) {
      return adapter?.payload?.enabled === false || adapter?.config?.enabled === false;
    },
    onAdapterClick(adapter) {
      if (adapter.listType === 'data_adapter') {
        this.$router.push({ name: 'project-data-adapter', params: { dataAdapter: adapter.key } });
      } else if (adapter.listType === 'ai_provider') {
        const modal = this.$showModal(CreateAIProviderModal, {
          projectId: this.projectId,
          editData: adapter,
          provider: adapter ? adapter.provider : adapter.key,
          providerName: adapter.name,
          onDelete: async () => {
            await this.onAIProviderDeleteClicked(adapter, modal);
          },
        });
      } else if (adapter.listType === 'email_provider') {
        const modal = this.$showModal(CreateEmailProviderModal, {
          projectId: this.projectId,
          editData: adapter,
          provider: adapter ? adapter.provider : adapter.key,
          providerName: adapter.name,
          onDelete: async () => {
            await this.onEmailProviderDeleteClicked(adapter, modal);
          },
        });
      } else if (adapter.listType === 'auth_provider') {
        this.$router.push({ name: 'project-authentication', params: { projectId: this.projectId }, query: { provider: adapter.key } });
      } else if (adapter.listType === 'logging_provider') {
        this.$showModal(CreateLoggingProviderModal, { projectId: this.projectId, editData: adapter, provider: adapter.key, providerName: adapter.name });
      }
    },
    onConnectClick(adapter) {
      if (adapter.listType === 'data_adapter') {
        this.$showModal(ConnectDataAdapter, { type: adapter.key, name: adapter.name });
      } else if (adapter.listType === 'ai_provider') {
        this.$showModal(CreateAIProviderModal, { projectId: this.projectId, provider: adapter.key, providerName: adapter.name });
      } else if (adapter.listType === 'email_provider') {
        this.$showModal(CreateEmailProviderModal, { projectId: this.projectId, provider: adapter.key, providerName: adapter.name });
      } else if (adapter.listType === 'auth_provider') {
        this.$router.push({ name: 'project-authentication', params: { projectId: this.projectId }, query: { provider: adapter.key } });
      } else if (adapter.listType === 'logging_provider') {
        this.$showModal(CreateLoggingProviderModal, { projectId: this.projectId, provider: adapter.key, providerName: adapter.name });
      }
    },
    onEmailProviderDeleteClicked(item, modal) {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.email_providers.remove_provider', { templateName: item.name }),
        info: null,
        onConfirm: async () => {
          await this.deleteProjectEmailProvider({ project_id: this.projectId, provider: item.provider });
          modal.close();
        },
      });
    },
    onAIProviderDeleteClicked(item, modal) {
      this.$showModal(DeleteModal, {
        subtitle: this.$t('projects.ai_providers.remove_provider', { templateName: item.name }),
        info: null,
        onConfirm: async () => {
          await this.deleteProjectAIProvider({ project_id: this.projectId, provider: item.provider });
          modal.close();
        },
      });
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        const promises = [];
        promises.push(this.fetchProjectDataAdapters({ projectId: this.projectId }));
        promises.push(this.fetchProjectAIProviders({ projectId: this.projectId }));
        promises.push(this.fetchProjectEmailProviders({ projectId: this.projectId }));
        promises.push(this.fetchProjectAuthProviders({ projectId: this.projectId }));
        promises.push(this.fetchProjectLoggingProviders({ projectId: this.projectId }));
        Promise.all(promises).then(() => {
          this.fetching = false;
        });
      },
      immediate: true,
    },
  },
};
</script>
